import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import type { User, Session } from "@supabase/supabase-js";

let supabasePromise: Promise<(typeof import("@/integrations/supabase/client"))["supabase"]> | null = null;

const getSupabase = async () => {
  if (!supabasePromise) {
    supabasePromise = import("@/integrations/supabase/client").then((mod) => mod.supabase);
  }

  return supabasePromise;
};

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, displayName: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    let cleanup: (() => void) | undefined;

    const bootstrap = async () => {
      try {
        const supabase = await getSupabase();
        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, nextSession) => {
          if (!mounted) return;
          setSession(nextSession);
          setUser(nextSession?.user ?? null);
          setLoading(false);
        });

        cleanup = () => subscription.unsubscribe();

        const { data: { session: currentSession } } = await supabase.auth.getSession();
        if (!mounted) return;
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
      } catch {
        if (!mounted) return;
      } finally {
        if (mounted) setLoading(false);
      }
    };

    // Bootstrap immediately — auth state is needed by route guards and most pages.
    // The previous idleCallback delay caused a 1-2s loading flicker on protected routes.
    void bootstrap();

    return () => {
      mounted = false;
      cleanup?.();
    };
  }, []);

  const signUp = async (email: string, password: string, displayName: string) => {
    const supabase = await getSupabase();
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { display_name: displayName },
        emailRedirectTo: window.location.origin,
      },
    });
    if (error) throw error;

    // Send welcome email
    try {
      await supabase.functions.invoke('send-transactional-email', {
        body: {
          templateName: 'welcome',
          recipientEmail: email,
          idempotencyKey: `welcome-${email}-${Date.now()}`,
          templateData: { displayName },
        },
      });
    } catch (e) {
      console.warn('Welcome email could not be sent:', e);
    }
  };

  const signIn = async (email: string, password: string) => {
    const supabase = await getSupabase();
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  };

  const signOut = async () => {
    const supabase = await getSupabase();
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signUp, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};
